import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Trophy, Target, Loader2, Swords, Share2, Gift, CheckCircle, RefreshCw, PartyPopper, HelpCircle, Mail, Download, Lock, ClipboardCheck, Facebook } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Challenge() {
  // Today's picks from PSL
  const [todaysPicks, setTodaysPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User's picks against PSL
  const [userPicks, setUserPicks] = useState({});
  
  // Results tracking
  const [results, setResults] = useState({});
  const [checkingResult, setCheckingResult] = useState(null);
  
  // Winner email capture modal
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [winnerEmail, setWinnerEmail] = useState('');
  const [winnerName, setWinnerName] = useState('');
  const [winningPick, setWinningPick] = useState(null);
  const [submittingWinner, setSubmittingWinner] = useState(false);
  
  // Downloaded apps tracking
  const [downloadedApps, setDownloadedApps] = useState({
    prizepicks: false,
    novig: false,
    betr: false
  });

  // Lock in picks modal
  const [showLockInModal, setShowLockInModal] = useState(false);
  const [lockInName, setLockInName] = useState('');
  const [lockInEmail, setLockInEmail] = useState('');
  const [submittingLockIn, setSubmittingLockIn] = useState(false);
  const [isLockedIn, setIsLockedIn] = useState(false);
  const [lockedInData, setLockedInData] = useState(null);

  // Rules modal
  const [showRulesModal, setShowRulesModal] = useState(false);
  
  // Load today's picks immediately
  useEffect(() => {
    fetchTodaysPicks();
    
    const savedApps = localStorage.getItem('challenge_downloaded_apps');
    if (savedApps) {
      setDownloadedApps(JSON.parse(savedApps));
    }
  }, []);

  const fetchTodaysPicks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/challenge/todays-picks`);
      const data = await response.json();
      setTodaysPicks(data.picks || []);
    } catch (error) {
      console.error('Failed to fetch picks:', error);
      toast.error('Failed to load today\'s picks');
    } finally {
      setLoading(false);
    }
  };

  const handleAppDownload = (app) => {
    const updated = { ...downloadedApps, [app]: !downloadedApps[app] };
    setDownloadedApps(updated);
    localStorage.setItem('challenge_downloaded_apps', JSON.stringify(updated));
  };

  // Lock in picks - save to database
  const handleLockInPicks = async () => {
    if (!lockInName.trim() || !lockInEmail.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    if (!lockInEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setSubmittingLockIn(true);
    try {
      const picksToSubmit = Object.entries(userPicks).map(([pickId, userPick]) => {
        const pick = todaysPicks.find(p => p.id === pickId);
        return {
          pick_id: pickId,
          player_name: pick?.player_name,
          prop_type: pick?.prop_type,
          line: pick?.line,
          psl_pick: pick?.psl_pick,
          user_pick: userPick
        };
      });

      const response = await fetch(`${API_URL}/api/challenge/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lockInName,
          email: lockInEmail,
          picks: picksToSubmit,
          downloaded_apps: Object.keys(downloadedApps).filter(app => downloadedApps[app])
        })
      });

      const data = await response.json();
      if (data.success) {
        setIsLockedIn(true);
        setLockedInData({
          name: lockInName,
          email: lockInEmail,
          picks: picksToSubmit,
          entry_id: data.entry_id,
          timestamp: new Date().toLocaleString()
        });
        setShowLockInModal(false);
        toast.success('Picks locked in! Good luck!');
        
        // Save to localStorage so they can't re-submit
        localStorage.setItem('challenge_locked_in', JSON.stringify({
          date: new Date().toDateString(),
          entry_id: data.entry_id
        }));

        // Automatically open free Winnable signup - just for playing!
        setTimeout(() => {
          window.open('https://www.winible.com/checkout/1547324105701741163?pid=1547324105714324076', '_blank');
        }, 1500);
      }
    } catch (error) {
      console.error('Lock in error:', error);
      toast.error('Failed to lock in picks');
    } finally {
      setSubmittingLockIn(false);
    }
  };

  // Check if already locked in today
  useEffect(() => {
    const savedLockIn = localStorage.getItem('challenge_locked_in');
    if (savedLockIn) {
      const { date } = JSON.parse(savedLockIn);
      if (date === new Date().toDateString()) {
        setIsLockedIn(true);
      }
    }
  }, []);

  const handleUserPick = (pickId, pick) => {
    setUserPicks(prev => ({ ...prev, [pickId]: pick }));
  };

  const isChallengingPSL = (pickId, pslPick) => {
    const userPick = userPicks[pickId];
    if (!userPick) return null;
    const pslPickLower = pslPick.toLowerCase();
    return userPick !== pslPickLower;
  };

  const checkPickResult = async (pickId) => {
    setCheckingResult(pickId);
    try {
      const response = await fetch(`${API_URL}/api/challenge/check-result/${pickId}`);
      const data = await response.json();
      
      if (data.status === 'verified') {
        const userPick = userPicks[pickId];
        const userWon = (userPick === 'over' && data.prop_hit) || (userPick === 'under' && !data.prop_hit);
        
        setResults(prev => ({
          ...prev,
          [pickId]: {
            verified: true,
            psl_won: data.psl_won,
            user_won: userWon,
            actual_value: data.actual_value,
            prop_hit: data.prop_hit,
            game_status: data.game_status
          }
        }));
        
        if (userWon && !data.psl_won) {
          const pick = todaysPicks.find(p => p.id === pickId);
          setWinningPick({ ...pick, actual_value: data.actual_value });
          setShowWinnerModal(true);
        } else if (userWon) {
          toast.success('Nice! The pick hit! 🎯');
        } else {
          toast.error('Missed this one. Try again!');
        }
      } else if (data.status === 'not_found') {
        toast.warning(data.message || 'Game hasn\'t started or player not playing');
      } else {
        toast.error(data.message || 'Could not verify result');
      }
    } catch (error) {
      console.error('Check result error:', error);
      toast.error('Failed to check result');
    } finally {
      setCheckingResult(null);
    }
  };

  const handleClaimReward = async () => {
    if (!winnerEmail.trim()) {
      toast.error('Enter your email to claim your reward!');
      return;
    }
    
    setSubmittingWinner(true);
    try {
      await fetch(`${API_URL}/api/challenge/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: winnerName || 'Winner',
          email: winnerEmail,
          picks: [{ pick_id: winningPick.id, user_pick: userPicks[winningPick.id], result: 'win' }],
          downloaded_apps: Object.keys(downloadedApps).filter(app => downloadedApps[app])
        })
      });
      
      // Free Winnable signup link
      const rewardUrl = 'https://www.winible.com/checkout/1547324105701741163?pid=1547324105714324076';
      
      toast.success('You\'re in! Check your email for next steps.');
      window.open(rewardUrl, '_blank');
      setShowWinnerModal(false);
      
    } catch (error) {
      console.error('Claim error:', error);
      toast.error('Failed to claim reward');
    } finally {
      setSubmittingWinner(false);
    }
  };

  const shareToTwitter = (pick, userPick) => {
    const isChallenge = userPick !== pick.psl_pick.toLowerCase();
    const emoji = userPick === 'over' ? '📈' : '📉';
    
    const tweetText = `${emoji} My pick: ${pick.player_name} ${userPick.toUpperCase()} ${pick.line} ${pick.prop_type}

${isChallenge ? "I'm going AGAINST @PrimeStatLab!" : "I'm riding WITH @PrimeStatLab!"}

Make your pick 👉 primestatlab.com/challenge`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://primestatlab.com/challenge')}&quote=${encodeURIComponent("I'm challenging @PrimeStatLab on today's picks! Think you can beat them? 🏀")}`;
    window.open(fbUrl, '_blank', 'width=550,height=420');
  };

  const countUserStats = () => {
    let wins = 0, losses = 0, pending = 0;
    
    Object.keys(userPicks).forEach(pickId => {
      const result = results[pickId];
      if (!result || !result.verified) pending++;
      else if (result.user_won) wins++;
      else losses++;
    });
    
    return { wins, losses, pending, total: Object.keys(userPicks).length };
  };

  const stats = countUserStats();
  const appsDownloaded = Object.values(downloadedApps).filter(Boolean).length;
  const allAppsDownloaded = appsDownloaded === 3;

  const affiliateLinks = [
    { name: 'PrizePicks', key: 'prizepicks', url: 'https://prizepicks.onelink.me/FjtC/d9ee5h8t', color: 'bg-green-500 hover:bg-green-600' },
    { name: 'Betr', key: 'betr', url: 'https://engagebetr.onelink.me/auSX/STATLAB', color: 'bg-purple-500 hover:bg-purple-600' },
    { name: 'No Vig', key: 'novig', url: 'https://novig.onelink.me/JHQQ/5wk3auau', color: 'bg-blue-500 hover:bg-blue-600' }
  ];

  // Calculate reward based on apps downloaded and win/loss
  const getRewardText = (didWin) => {
    if (didWin) {
      if (appsDownloaded === 3) return '1 FREE Month Premium!';
      if (appsDownloaded === 2) return '2 FREE Weeks Premium!';
      if (appsDownloaded === 1) return '1 FREE Week Premium!';
      return 'Free Winnable Account!';
    } else {
      if (appsDownloaded > 0) return '3 FREE Days Premium!';
      return 'Free Winnable Account!';
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Challenge Me - Beat My Picks Today | PrimeStatLab</title>
        <meta name="description" content="Think you know better? Challenge PrimeStatLab's daily picks. Beat me and win free premium picks!" />
        <link rel="canonical" href="https://primestatlab.com/challenge" />
      </Helmet>
      
      <div className="container mx-auto px-6 py-12" data-testid="challenge-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Swords className="w-10 h-10 text-[#10b981]" />
            <h1 className="text-4xl font-black tracking-tight">CHALLENGE ME</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-4">
            Think you can beat my picks? Play against me and win rewards!
          </p>
          
          {/* Rules Button */}
          <Dialog open={showRulesModal} onOpenChange={setShowRulesModal}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10">
                <HelpCircle className="w-4 h-4 mr-2" />
                Rules & Rewards
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl text-center text-[#10b981]">Rules & Rewards</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* How to Play */}
                <div>
                  <h3 className="font-bold text-lg text-white mb-3">How to Play</h3>
                  <ol className="space-y-2 text-sm text-gray-300">
                    <li>1. Pick <span className="text-green-400">OVER</span> or <span className="text-red-400">UNDER</span> on any of today's picks</li>
                    <li>2. Click <span className="text-yellow-400">"Check Result"</span> after the game ends</li>
                    <li>3. If you beat me, claim your reward!</li>
                  </ol>
                </div>

                {/* Rewards Table */}
                <div>
                  <h3 className="font-bold text-lg text-white mb-3">Rewards</h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <div className="text-[#10b981] font-bold">Just for Playing</div>
                      <div className="text-sm text-gray-400">Free Winnable account with access to all my free picks & analysis</div>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <div className="text-yellow-400 font-bold">Lose + Sign Up for Any App</div>
                      <div className="text-sm text-gray-400">3 FREE days of premium picks</div>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg border border-green-500/30">
                      <div className="text-green-400 font-bold">Beat Me + 1 App Sign-Up</div>
                      <div className="text-sm text-gray-400">1 FREE week of premium picks</div>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg border border-green-500/50">
                      <div className="text-green-400 font-bold">Beat Me + 2 App Sign-Ups</div>
                      <div className="text-sm text-gray-400">2 FREE weeks of premium picks</div>
                    </div>
                    <div className="p-3 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg border border-yellow-500">
                      <div className="text-yellow-400 font-bold">Beat Me + 3 App Sign-Ups</div>
                      <div className="text-sm text-white">1 FREE MONTH of premium picks!</div>
                    </div>
                  </div>
                </div>

                {/* How to Claim */}
                <div className="p-4 bg-zinc-800 rounded-lg border border-[#10b981]/50">
                  <h3 className="font-bold text-lg text-[#10b981] mb-2 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    How to Claim Your Reward
                  </h3>
                  <p className="text-sm text-gray-300 mb-2">
                    Text a screenshot of your app sign-ups to:
                  </p>
                  <div className="bg-zinc-900 p-3 rounded text-center">
                    <span className="text-[#10b981] font-mono font-bold text-lg">(346) 326-4149</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Include screenshots showing you signed up for PrizePicks, Betr, or No Vig!
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Bar */}
        {stats.total > 0 && (
          <div className="flex justify-center gap-4 mb-6">
            <Badge variant="outline" className="border-green-500 text-green-400 text-lg px-4 py-2">
              Wins: {stats.wins}
            </Badge>
            <Badge variant="outline" className="border-red-500 text-red-400 text-lg px-4 py-2">
              Losses: {stats.losses}
            </Badge>
            <Badge variant="outline" className="border-yellow-500 text-yellow-400 text-lg px-4 py-2">
              Pending: {stats.pending}
            </Badge>
          </div>
        )}

        {/* Download Apps Section */}
        <Card className="max-w-4xl mx-auto mb-8 bg-zinc-900 border-zinc-800">
          <CardContent className="py-6">
            <div className="text-center mb-4">
              <Download className="w-6 h-6 text-[#10b981] mx-auto mb-2" />
              <h3 className="text-lg font-bold">Sign Up for Apps = Bonus Rewards</h3>
              <p className="text-gray-500 text-sm">More sign-ups = bigger rewards when you win!</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              {affiliateLinks.map((link) => (
                <div key={link.key} className="flex items-center gap-3 bg-zinc-800 rounded-lg p-3">
                  <Checkbox
                    id={link.key}
                    checked={downloadedApps[link.key]}
                    onCheckedChange={() => handleAppDownload(link.key)}
                    data-testid={`checkbox-${link.key}`}
                  />
                  <label htmlFor={link.key} className="text-sm cursor-pointer">
                    {downloadedApps[link.key] ? (
                      <span className="text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> {link.name}
                      </span>
                    ) : (
                      <span>{link.name}</span>
                    )}
                  </label>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className={link.color}>
                      Sign Up
                    </Button>
                  </a>
                </div>
              ))}
            </div>
            
            {appsDownloaded > 0 && (
              <div className="text-center mt-4">
                <Badge className={`${allAppsDownloaded ? 'bg-yellow-500 text-black' : 'bg-[#10b981]'} px-4 py-2`}>
                  {appsDownloaded}/3 Apps → {allAppsDownloaded ? 'MAX BONUS: 1 Month!' : appsDownloaded === 2 ? 'Beat me for 2 weeks!' : 'Beat me for 1 week!'}
                </Badge>
              </div>
            )}
            
            <p className="text-center text-xs text-gray-500 mt-3">
              After signing up, text screenshot to <span className="text-[#10b981]">(346) 326-4149</span>
            </p>
          </CardContent>
        </Card>

        {/* Today's Picks */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Target className="w-6 h-6 text-[#10b981]" />
              Today's Picks
            </h2>
            <Button variant="outline" size="sm" onClick={fetchTodaysPicks} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#10b981]" />
              <p className="text-gray-400 mt-2">Loading picks...</p>
            </div>
          ) : todaysPicks.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Target className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-xl font-bold text-gray-400 mb-2">No active picks right now</p>
                <p className="text-gray-500 mb-4">Picks appear once they're added to the board!</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {affiliateLinks.map((link) => (
                    <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className={link.color}>{link.name}</Button>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {todaysPicks.map((pick) => {
                const userPick = userPicks[pick.id];
                const isChallenge = isChallengingPSL(pick.id, pick.psl_pick);
                const result = results[pick.id];
                
                return (
                  <Card 
                    key={pick.id} 
                    className={`bg-zinc-900 border-zinc-800 transition-all ${
                      result?.verified 
                        ? result.user_won 
                          ? 'border-yellow-500 bg-yellow-900/10' 
                          : 'border-red-500/50 bg-red-900/10'
                        : isChallenge === true 
                          ? 'border-orange-500/50' 
                          : isChallenge === false 
                            ? 'border-green-500/50' 
                            : ''
                    }`}
                    data-testid={`pick-card-${pick.id}`}
                  >
                    <CardContent className="py-5">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">{pick.sport}</Badge>
                            <Badge className="bg-[#10b981] text-white">{pick.confidence_level}/10</Badge>
                          </div>
                          <h3 className="font-bold text-xl">{pick.player_name}</h3>
                          <p className="text-gray-400">
                            {pick.prop_type} • Line: <span className="text-white font-mono text-lg">{pick.line}</span>
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-sm text-gray-500">My Pick:</span>
                            <Badge className={pick.psl_pick.toLowerCase() === 'over' ? 'bg-green-600' : 'bg-red-600'}>
                              {pick.psl_pick.toUpperCase()}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <div className="text-xs text-gray-500 text-center">Your Pick:</div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleUserPick(pick.id, 'over')}
                              variant={userPick === 'over' ? 'default' : 'outline'}
                              size="lg"
                              className={userPick === 'over' ? 'bg-green-600 hover:bg-green-700' : 'border-green-500 text-green-400 hover:bg-green-900/30'}
                              disabled={result?.verified || isLockedIn}
                              data-testid={`pick-over-${pick.id}`}
                            >
                              <TrendingUp className="w-5 h-5 mr-1" />
                              OVER
                            </Button>
                            <Button
                              onClick={() => handleUserPick(pick.id, 'under')}
                              variant={userPick === 'under' ? 'default' : 'outline'}
                              size="lg"
                              className={userPick === 'under' ? 'bg-red-600 hover:bg-red-700' : 'border-red-500 text-red-400 hover:bg-red-900/30'}
                              disabled={result?.verified || isLockedIn}
                              data-testid={`pick-under-${pick.id}`}
                            >
                              <TrendingDown className="w-5 h-5 mr-1" />
                              UNDER
                            </Button>
                          </div>
                          
                          {result?.verified ? (
                            <Badge className={`justify-center text-sm ${result.user_won ? 'bg-yellow-500 text-black' : 'bg-red-600 text-white'}`}>
                              {result.user_won ? `🏆 YOU WON! (${result.actual_value})` : `❌ Missed (${result.actual_value})`}
                            </Badge>
                          ) : isChallenge !== null && (
                            <Badge variant="outline" className={`justify-center ${isChallenge ? 'border-orange-500 text-orange-400' : 'border-green-500 text-green-400'}`}>
                              {isChallenge ? <><Swords className="w-3 h-3 mr-1" /> CHALLENGING ME</> : <><CheckCircle className="w-3 h-3 mr-1" /> RIDING WITH ME</>}
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 min-w-[120px]">
                          {userPick && !result?.verified && isLockedIn && (
                            <Button
                              onClick={() => checkPickResult(pick.id)}
                              variant="outline"
                              className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                              disabled={checkingResult === pick.id}
                              data-testid={`check-result-${pick.id}`}
                            >
                              {checkingResult === pick.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Target className="w-4 h-4 mr-1" />}
                              Check Result
                            </Button>
                          )}
                          
                          {userPick && (
                            <div className="flex gap-1">
                              <Button onClick={() => shareToTwitter(pick, userPick)} size="sm" className="bg-blue-500 hover:bg-blue-600" title="Share on X/Twitter">
                                <Share2 className="w-4 h-4" />
                              </Button>
                              <Button onClick={shareToFacebook} size="sm" className="bg-blue-700 hover:bg-blue-800" title="Share on Facebook">
                                <Facebook className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Lock In Picks Button */}
              {Object.keys(userPicks).length > 0 && !isLockedIn && (
                <div className="pt-4">
                  <Button
                    onClick={() => setShowLockInModal(true)}
                    className="w-full h-14 text-lg bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857]"
                    data-testid="lock-in-button"
                  >
                    <Lock className="w-5 h-5 mr-2" />
                    Lock In {Object.keys(userPicks).length} Pick{Object.keys(userPicks).length > 1 ? 's' : ''} - Sign Up to Track
                  </Button>
                </div>
              )}

              {/* Locked In Confirmation */}
              {isLockedIn && (
                <Card className="bg-gradient-to-r from-[#10b981]/20 to-[#059669]/20 border-[#10b981]">
                  <CardContent className="py-6">
                    <div className="text-center">
                      <ClipboardCheck className="w-12 h-12 text-[#10b981] mx-auto mb-3" />
                      <h3 className="text-xl font-bold text-[#10b981] mb-2">Picks Locked In!</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Check results after games end. <br/>
                        <span className="text-white font-medium">Be on the lookout for an email from PrimeStatLab with your access!</span>
                      </p>
                      {lockedInData && (
                        <div className="bg-zinc-900 rounded-lg p-4 text-left max-w-md mx-auto">
                          <p className="text-xs text-gray-500 mb-2">Your Receipt:</p>
                          <p className="text-sm"><span className="text-gray-500">Name:</span> {lockedInData.name}</p>
                          <p className="text-sm"><span className="text-gray-500">Email:</span> {lockedInData.email}</p>
                          <p className="text-sm"><span className="text-gray-500">Picks:</span> {lockedInData.picks?.length || Object.keys(userPicks).length}</p>
                          <p className="text-xs text-gray-600 mt-2">{lockedInData.timestamp}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Quick Rewards Summary */}
        <div className="max-w-4xl mx-auto mt-8 text-center">
          <p className="text-gray-500 text-sm">
            <span className="text-[#10b981]">Just for playing</span> → Free Winnable account • 
            <span className="text-yellow-400 ml-2">Beat me + apps</span> → Up to 1 month premium!
          </p>
          <Button variant="link" className="text-[#10b981]" onClick={() => setShowRulesModal(true)}>
            See all rewards →
          </Button>
        </div>

        {/* Share Your Session - Only show if user has made picks */}
        {Object.keys(userPicks).length > 0 && (
          <div className="max-w-md mx-auto mt-8">
            <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-[#10b981]/30">
              <CardContent className="p-4">
                <div className="text-center mb-3">
                  <h3 className="font-bold text-[#10b981] flex items-center justify-center gap-2">
                    <Share2 className="w-4 h-4" />
                    Share Your Session
                  </h3>
                  <p className="text-xs text-gray-400">Brag about your picks!</p>
                </div>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div className="text-center">
                    <span className="text-2xl font-bold text-green-400">{stats.wins}</span>
                    <p className="text-xs text-gray-500">Wins</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-red-400">{stats.losses}</span>
                    <p className="text-xs text-gray-500">Losses</p>
                  </div>
                  {stats.pending > 0 && (
                    <div className="text-center">
                      <span className="text-2xl font-bold text-yellow-400">{stats.pending}</span>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                  )}
                </div>
                <Button 
                  onClick={() => {
                    const winRate = stats.wins + stats.losses > 0 
                      ? Math.round((stats.wins / (stats.wins + stats.losses)) * 100) 
                      : 0;
                    const tweetText = `📊 My @PrimeStatLab Challenge Session:

✅ ${stats.wins} Wins | ❌ ${stats.losses} Losses${stats.pending > 0 ? ` | ⏳ ${stats.pending} Pending` : ''}
${stats.wins > stats.losses ? '🏆 BEATING THE LAB!' : stats.wins === stats.losses ? '⚔️ Tied up!' : '💪 Next time!'}

Think you can beat them? 👉 primestatlab.com/challenge

#SportsBetting #PlayerProps #NBA`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank', 'width=550,height=420');
                  }}
                  className="w-full bg-black hover:bg-zinc-800 text-white"
                  data-testid="share-session-twitter"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  Share on X
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Winner Modal */}
        <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
          <DialogContent className="bg-zinc-900 border-yellow-500 max-w-md">
            <DialogHeader>
              <div className="text-center">
                <PartyPopper className="w-16 h-16 text-yellow-400 mx-auto mb-4 animate-bounce" />
                <DialogTitle className="text-3xl text-yellow-400">YOU BEAT ME! 🏆</DialogTitle>
                <p className="text-gray-300 mt-2">
                  Nice call on {winningPick?.player_name}! Actual: {winningPick?.actual_value}
                </p>
              </div>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* SHARE YOUR WIN - New Section */}
              <div className="p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg border border-green-500/50">
                <p className="text-center text-sm text-green-400 font-bold mb-3">🔥 BRAG ABOUT IT!</p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      const tweetText = `🏆 I JUST BEAT @PrimeStatLab! 🏆

Called ${winningPick?.player_name} ${userPicks[winningPick?.id]?.toUpperCase()} ${winningPick?.line} ${winningPick?.prop_type}
✅ Actual: ${winningPick?.actual_value}

Think YOU can beat them? 👉 primestatlab.com/challenge

#SportsBetting #PlayerProps #NBA`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank', 'width=550,height=420');
                    }}
                    className="flex-1 bg-black hover:bg-zinc-800 text-white"
                    data-testid="share-win-twitter"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Share on X
                  </Button>
                  <Button 
                    onClick={() => {
                      const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://primestatlab.com/challenge')}&quote=${encodeURIComponent(`🏆 I JUST BEAT @PrimeStatLab! Called ${winningPick?.player_name} correctly! Think YOU can beat them?`)}`;
                      window.open(fbUrl, '_blank', 'width=550,height=420');
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="share-win-facebook"
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg border border-yellow-500/50">
                <div className="text-center mb-4">
                  <h3 className="font-bold text-xl text-yellow-400">{getRewardText(true)}</h3>
                  <p className="text-sm text-gray-400">Enter your email to get started</p>
                </div>
                
                <div className="space-y-3">
                  <Input
                    placeholder="Your Name (optional)"
                    value={winnerName}
                    onChange={(e) => setWinnerName(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={winnerEmail}
                    onChange={(e) => setWinnerEmail(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="winner-email-input"
                  />
                  
                  <Button 
                    onClick={handleClaimReward}
                    disabled={submittingWinner}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold h-12"
                    data-testid="claim-reward-button"
                  >
                    {submittingWinner ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Gift className="w-5 h-5 mr-2" />}
                    Claim My Reward!
                  </Button>
                </div>
              </div>
              
              <div className="p-3 bg-zinc-800 rounded text-center">
                <p className="text-xs text-gray-400 mb-1">Text screenshot of app sign-ups to:</p>
                <p className="text-[#10b981] font-mono font-bold">(346) 326-4149</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Lock In Picks Modal */}
        <Dialog open={showLockInModal} onOpenChange={setShowLockInModal}>
          <DialogContent className="bg-zinc-900 border-[#10b981] max-w-md">
            <DialogHeader>
              <div className="text-center">
                <Lock className="w-12 h-12 text-[#10b981] mx-auto mb-3" />
                <DialogTitle className="text-2xl text-[#10b981]">Lock In Your Picks</DialogTitle>
                <p className="text-gray-400 mt-2 text-sm">
                  Enter your info to record your picks and track your results
                </p>
              </div>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Preview of picks */}
              <div className="bg-zinc-800 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">Your Picks:</p>
                {Object.entries(userPicks).map(([pickId, userPick]) => {
                  const pick = todaysPicks.find(p => p.id === pickId);
                  if (!pick) return null;
                  return (
                    <div key={pickId} className="flex justify-between items-center py-1 border-b border-zinc-700 last:border-0">
                      <span className="text-sm">{pick.player_name}</span>
                      <Badge className={userPick === 'over' ? 'bg-green-600' : 'bg-red-600'}>
                        {userPick.toUpperCase()}
                      </Badge>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3">
                <Input
                  placeholder="Your Name"
                  value={lockInName}
                  onChange={(e) => setLockInName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="lock-in-name"
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={lockInEmail}
                  onChange={(e) => setLockInEmail(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="lock-in-email"
                />
                
                <Button 
                  onClick={handleLockInPicks}
                  disabled={submittingLockIn}
                  className="w-full bg-[#10b981] hover:bg-[#059669] h-12 font-bold"
                  data-testid="confirm-lock-in"
                >
                  {submittingLockIn ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Lock className="w-5 h-5 mr-2" />
                  )}
                  Lock In My Picks
                </Button>
              </div>
              
              <p className="text-center text-xs text-gray-500">
                Your picks will be recorded and you can check results after games end
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
